import { useCallback, useState } from 'react';
import { IoClose } from 'react-icons/io5';
import { RadioGroup, Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Radio, Spinner } from '@ds';
import { CS_REMOVAL_TEXT, RESOURCE_REMOVE_TEXT, RESOURCE_SWAP_TEXT } from '@lp/features/RoutePlanner/config';
import { TmdxResourceCard } from '@features/components/ResourceCards';
import { ZonedDateTime } from '@internationalized/date';
import { CaseClinicalSpecialistResource, CaseResource, OrganType } from '@/api/apis/case';
import { CSAssignmentCandidate, EmployeeComposed } from '@/api/apis/resources';
import { RESOURCE_STATUS_TEXT_MAP } from '@/constants/resources.constants';
import { RemoveClinicalSpecialistModalContent } from './RemoveClinicalSpecialistModalContent';
import { ActiveGroundTripWarning } from './ActiveGroundTripWarning';

export type RemoveClinicalSpecialistModalProps = {
  title: string;
  resource?: CaseResource<CaseClinicalSpecialistResource> | null;
  organType: OrganType;
  isOpen: boolean;
  isLoading: boolean;
  onAction: (data: CSAssignmentCandidate, cb: () => void, goTime?: ZonedDateTime) => void;
  onConfirmation: (removeType?: 'remove' | 'swap', newResourceId?: string) => void;
  onClose: () => void;
};

export const RemoveClinicalSpecialistModal = ({
  title,
  resource,
  organType,
  isOpen,
  isLoading,
  onAction,
  onConfirmation,
  onClose,
}: RemoveClinicalSpecialistModalProps) => {
  const [removeType, setRemoveType] = useState<'remove' | 'swap' | null>(null);
  const [newResource, setNewResource] = useState<CSAssignmentCandidate | null>(null);
  const [selectedGoTime, setSelectedGoTime] = useState<ZonedDateTime | undefined>();

  const handleSelectNewResource = useCallback(
    (employee: CSAssignmentCandidate, _cb: () => void, goTime?: ZonedDateTime) => {
      setNewResource(employee);
      setSelectedGoTime(goTime);
    },
    [],
  );

  const handleResourceRemove = async () => {
    if (removeType === 'swap' && newResource) {
      onAction(newResource, () => onConfirmation(removeType, newResource.employeeId), selectedGoTime);
    } else if (removeType) {
      onConfirmation(removeType, newResource?.employeeId);
    }
  };

  const onTabChange = (value: string) => {
    setRemoveType(value as 'remove' | 'swap');
  };

  const modalSize = removeType === 'swap' ? '5xl' : 'md';

  return (
    <Modal
      size={modalSize}
      isOpen={isOpen}
      onClose={onClose}
      backdrop="opaque"
      closeButton={<IoClose size={45} style={{ top: 15 }} />}
      classNames={{
        base: `px-6 max-h-[85svh] ${removeType === 'swap' ? 'w-auto max-w-fit' : ''}`,
        body: `${removeType === 'swap' ? 'min-w-[1100px]' : ''} border-y-1 border-base-30 px-0 py-6 text-l-r overflow-y-auto`,
        header: 'px-0 font-secondary text-hl! text-base-20',
        footer: 'px-0 flex gap-6',
        wrapper: 'z-420',
        backdrop: 'z-419',
      }}
    >
      <ModalContent>
        {() => (
          <>
            {isLoading && (
              <div className="absolute top-0 left-0 flex justify-center items-center z-50 w-full h-full bg-white bg-opacity-40">
                <Spinner size="md" />
              </div>
            )}
            <ModalHeader>{title}</ModalHeader>
            <ModalBody>
              <TmdxResourceCard
                label="Actions"
                list={[]}
                type="user"
                title={`${(resource!.data! as EmployeeComposed).firstName} ${(resource!.data! as EmployeeComposed).lastName}`}
                subTitle="Clinical Specialist"
                phoneNumber={(resource!.data! as EmployeeComposed).phone}
                avatarProps={{
                  src: (resource!.data! as EmployeeComposed).profilePhoto,
                }}
                chipProps={{
                  status: resource!.logisticsStatus,
                }}
                chipText={RESOURCE_STATUS_TEXT_MAP[resource!.logisticsStatus]}
                isReadOnly
                menuDisabled
                style={{ overflow: 'initial' }}
              />
              <div className="flex flex-col gap-4 pt-6 pb-3">
                <ActiveGroundTripWarning resourceId={resource?.resourceId} />
                <span className="text-m-r text-base-5">{CS_REMOVAL_TEXT}</span>
                <RadioGroup className="gap-2 p-0" value={removeType} onValueChange={onTabChange}>
                  <Radio size="md" value="remove">
                    {RESOURCE_REMOVE_TEXT}
                  </Radio>
                  <Radio size="md" value="swap">
                    {RESOURCE_SWAP_TEXT}
                  </Radio>
                </RadioGroup>
                {removeType === 'swap' && (
                  <RemoveClinicalSpecialistModalContent
                    organType={organType}
                    resource={resource!}
                    selectedResourceId={newResource?.employeeId}
                    onAction={handleSelectNewResource}
                    isSwap
                  />
                )}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button isDisabled={isLoading} size="md" color="default" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                isDisabled={!removeType || isLoading || (removeType === 'swap' && !newResource)}
                size="md"
                color="default"
                onClick={handleResourceRemove}
              >
                {removeType === 'swap' ? 'Remove and swap' : 'Remove'}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
